/**
 * GateManagement Component - DDD 리팩토링 버전
 * 게이트(출입구) 관리 컴포넌트
 */
import React from 'react';
import { Card, Button } from '../../../components/ui';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useGateManagement } from '../use-cases/useGateManagement';
import { gateManagementService } from '../services/GateManagementService';

const GateManagement: React.FC = () => {
  const {
    gates,
    loading,
    showAddModal,
    setShowAddModal,
    newGate,
    setNewGate,
    handleAddGate,
    handleDeleteGate,
    handleUpdateAlertSettings,
  } = useGateManagement();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  const renderGateCard = (gate: any) => (
    <Card key={gate.id} className="p-6">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900">{gate.name}</h3>
        <p className="text-sm text-gray-500">{gate.location}</p>
      </div>
      <div className="mb-4 flex items-center gap-2">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${gateManagementService.getGateTypeColor(gate.type)}`}>
          {gateManagementService.getGateTypeLabel(gate.type)}
        </span>
        {gate.direction && (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${gateManagementService.getGateDirectionColor(gate.direction)}`}>
            {gateManagementService.getGateDirectionLabel(gate.direction)}
          </span>
        )}
      </div>
      {gate.isEsp32 ? (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-blue-700 font-medium">📡 ESP32 IP</span>
            <span className="text-sm text-blue-900 font-mono">{gate.ipAddress || '미설정'}</span>
          </div>
          <p className="text-xs text-blue-600">태블릿 앱에서 자동 등록된 릴레이 게이트</p>
        </div>
      ) : gate.alertSettings && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">부모님 알림</span>
            <input
              type="checkbox"
              checked={gate.alertSettings.sendParentAlert}
              onChange={(e) => handleUpdateAlertSettings(gate.id, e.target.checked, gate.alertSettings.countForStudyTime)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">순공부시간 계산</span>
            <input
              type="checkbox"
              checked={gate.alertSettings.countForStudyTime}
              onChange={(e) => handleUpdateAlertSettings(gate.id, gate.alertSettings.sendParentAlert, e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          </div>
        </div>
      )}
      <div className="flex items-center justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleDeleteGate(gate.id)}
          className="text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <TrashIcon className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">게이트 관리</h1>
            <p className="mt-2 text-sm text-gray-600">출입구 게이트를 관리합니다</p>
          </div>
          <Button onClick={() => setShowAddModal(true)} className="flex items-center gap-2">
            <PlusIcon className="h-5 w-5" />새 게이트 추가
          </Button>
        </div>
      </div>

      {/* 출입구 게이트 (얼굴인식) 섹션 */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-base font-semibold text-gray-700">🚪 출입구 게이트 (얼굴인식)</h2>
          <span className="text-xs text-gray-500">
            {gates.filter(g => !g.isEsp32).length}개
          </span>
        </div>
        {gates.filter(g => !g.isEsp32).length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg text-sm text-gray-400">
            등록된 출입구 게이트가 없습니다. "새 게이트 추가"로 등록하세요.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {gates.filter(g => !g.isEsp32).map((gate) => renderGateCard(gate))}
          </div>
        )}
      </div>

      {/* ESP32 릴레이 게이트 섹션 */}
      {gates.some(g => g.isEsp32) && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-base font-semibold text-blue-700">📡 ESP32 릴레이 게이트 (자동문 제어)</h2>
            <span className="text-xs text-gray-500">
              {gates.filter(g => g.isEsp32).length}개
            </span>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            태블릿 앱의 ESP32 등록 마법사로 자동 등록된 게이트입니다. IP만 표시되며 설정은 태블릿에서 합니다.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {gates.filter(g => g.isEsp32).map((gate) => renderGateCard(gate))}
          </div>
        </div>
      )}

      {/* 게이트 추가 모달 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">새 게이트 추가</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  게이트 이름
                </label>
                <input
                  type="text"
                  value={newGate.name}
                  onChange={(e) => setNewGate({ ...newGate, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="예: 메인 출입구"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">위치</label>
                <input
                  type="text"
                  value={newGate.location}
                  onChange={(e) => setNewGate({ ...newGate, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="예: 1층 현관"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">타입</label>
                <select
                  value={newGate.type}
                  onChange={(e) =>
                    setNewGate({ ...newGate, type: e.target.value as any })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="main_entrance">메인 출입구</option>
                  <option value="study_room">열람실</option>
                  <option value="restroom">화장실</option>
                  <option value="lounge">휴게실</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">방향</label>
                <select
                  value={newGate.direction || 'entry'}
                  onChange={(e) =>
                    setNewGate({ ...newGate, direction: e.target.value as any })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="entry">입구만</option>
                  <option value="exit">출구만</option>
                  <option value="both">입출구 겸용</option>
                </select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">부모님 알림 전송</span>
                  <input
                    type="checkbox"
                    checked={newGate.alertSettings.sendParentAlert}
                    onChange={(e) =>
                      setNewGate({
                        ...newGate,
                        alertSettings: {
                          ...newGate.alertSettings,
                          sendParentAlert: e.target.checked,
                        },
                      })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">순공부시간 계산</span>
                  <input
                    type="checkbox"
                    checked={newGate.alertSettings.countForStudyTime}
                    onChange={(e) =>
                      setNewGate({
                        ...newGate,
                        alertSettings: {
                          ...newGate.alertSettings,
                          countForStudyTime: e.target.checked,
                        },
                      })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <Button onClick={handleAddGate} className="flex-1">
                추가
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowAddModal(false)}
                className="flex-1"
              >
                취소
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GateManagement;
